import { BASE_KIOSK_API } from '@/core/api-config';
import { TokenManager } from '@/core/auth/token-manager';
import { ApiErrorMiddleware, ApiUnauthorizedError } from '@/core/api-error-middleware';
import type { KioskOperatorRecord } from '../types/kiosk-flow-types';

export const KIOSK_ADMIN_UNAUTHORIZED =
  'The Kiosk API refused the request (401) — publishing needs Kiosk API admin access';

// Kiosk API operator settings — the same endpoints the Kiosk Customizer uses (kiosk-config-api.ts).
// The operator is in the query/body; the admin endpoints take no operator headers.
export class KioskFlowPushApi {
  constructor(
    private readonly baseUrl: string = BASE_KIOSK_API,
    private readonly tokenManager: TokenManager
  ) {}

  private async getAuthHeaders(): Promise<Record<string, string>> {
    return { 'Content-Type': 'application/json', ...(await this.tokenManager.getAuthHeaders()) };
  }

  // The admin endpoints don't use the back-office session, so their 401 must not log the user out
  private async fetch(url: string, options: RequestInit, apiName: string, endpoint: string): Promise<Response> {
    try {
      return await ApiErrorMiddleware.fetchWithErrorHandling(url, options, apiName, endpoint, undefined, false);
    } catch (error) {
      if (error instanceof ApiUnauthorizedError) throw new Error(KIOSK_ADMIN_UNAUTHORIZED);
      throw error;
    }
  }

  async getOperator(operatorId: number): Promise<KioskOperatorRecord> {
    const response = await this.fetch(
      `${this.baseUrl}/admin/operator?operatorId=${operatorId}`,
      { method: 'GET', headers: await this.getAuthHeaders() },
      'KioskFlowPushApi.getOperator',
      'GET /admin/operator'
    );
    return response.json();
  }

  // notify=true makes every kiosk of the operator restart with the new settings — even mid-session
  async saveOperatorAndNotify(operator: KioskOperatorRecord): Promise<void> {
    await this.fetch(
      `${this.baseUrl}/admin/addOperator?notify=true`,
      { method: 'POST', headers: await this.getAuthHeaders(), body: JSON.stringify(operator) },
      'KioskFlowPushApi.saveOperatorAndNotify',
      'POST /admin/addOperator'
    );
  }
}
